import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Helmet } from 'react-helmet-async';
import { blogCategories, fetchArticles, formatArticleDate } from '../services/strapiBlog';

const defaultCover = '/images/symuliga.webp';
const PAGE_SIZE = 9;

const BlogList = () => {
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageCount: 1, total: 0 });
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError(null);

    fetchArticles({ page: pagination.page, pageSize: PAGE_SIZE, category })
      .then((result) => {
        if (!active) return;
        setArticles(result.articles);
        setPagination(result.pagination);
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('Strapi articles request failed:', requestError);
        setError('Nie udało się pobrać artykułów z CMS. Spróbuj ponownie później.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [pagination.page, category]);

  const handleCategoryChange = (nextCategory) => {
    setCategory(nextCategory);
    setPagination((current) => ({ ...current, page: 1 }));
  };

  return (
    <>
      <Helmet>
        <title>Blog i aktualności | SymuLiga.pl</title>
        <meta
          name="description"
          content="Analizy, raporty i aktualności o symulacjach piłkarskich, polskich ligach oraz technologii stojącej za SymuLigą."
        />
        <link rel="canonical" href={`${window.location.origin}/blog`} />
      </Helmet>

      <Box sx={{ width: '100%', maxWidth: 1180, mx: 'auto', px: { xs: 1, md: 2 }, py: { xs: 2, md: 4 } }}>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Typography variant="h4">Blog i aktualności</Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 760 }}>
            Analizy, raporty i aktualności o symulacjach piłkarskich, polskich ligach oraz technologii stojącej za SymuLigą.
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
          {blogCategories.map((item) => (
            <Chip
              key={item.slug}
              label={item.label}
              clickable
              onClick={() => handleCategoryChange(item.slug)}
              color={category === item.slug ? 'warning' : 'default'}
              variant={category === item.slug ? 'filled' : 'outlined'}
              sx={{ borderRadius: '6px' }}
            />
          ))}
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress color="warning" />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : articles.length === 0 ? (
          <Alert severity="info">Brak artykułów w tej kategorii.</Alert>
        ) : (
          <>
            <Grid container spacing={3}>
              {articles.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: '#2c2c2c',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                    }}
                  >
                    <CardActionArea component={Link} to={`/blog/${article.slug}`} sx={{ height: '100%' }}>
                      <CardMedia
                        component="img"
                        height="178"
                        image={article.cover?.url || defaultCover}
                        alt={article.cover?.alternativeText || article.title}
                        sx={{ objectFit: 'cover', backgroundColor: '#1a1a1a' }}
                      />
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 210 }}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                          {article.category?.name && <Chip label={article.category.name} size="small" />}
                          {article.author?.name && <Chip label={article.author.name} size="small" variant="outlined" />}
                          {article.publishedAt && <Chip label={formatArticleDate(article.publishedAt)} size="small" variant="outlined" />}
                        </Stack>
                        <Typography variant="h6" sx={{ lineHeight: 1.25, mb: 1 }}>
                          {article.title}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mb: 2, flexGrow: 1 }}>
                          {article.description}
                        </Typography>
                        <Button
                          size="small"
                          endIcon={<ArrowForwardIcon />}
                          sx={{ alignSelf: 'flex-start', color: '#FFD700' }}
                        >
                          Czytaj
                        </Button>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {pagination.pageCount > 1 && (
              <Stack alignItems="center" sx={{ mt: 4 }}>
                <Pagination
                  count={pagination.pageCount}
                  page={pagination.page}
                  onChange={(_, page) => setPagination((current) => ({ ...current, page }))}
                  color="warning"
                />
              </Stack>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default BlogList;
